import { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import { Badge, Center, ScrollArea, Tabs } from '@mantine/core';
import { IconUser, IconSearch, IconBasket, IconQuestionMark, IconReport, IconFiles, IconMessages } from '@tabler/icons';

import { Header } from './header';
import { Brief } from './brief';
import { Search } from './search';
import { Basket } from './basket';
import { FormCreator } from '../../shared/FormCreator';
import { Process } from './process';
import { Docs } from './docs';
import { Messages } from '../../shared/MessagesContainer/messages';
import { useTranslation } from 'react-i18next';

type ParamInterface = {
    paramNeedId: string,
}

const ProcessNeed = () => {

    const { paramNeedId } = useParams<ParamInterface>();
    const needId: number = parseInt(paramNeedId === undefined ? '0' : paramNeedId);
    const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

    const [activeTab, setActiveTab] = useState<string | null>('brief');
    const [isBriefOpen, setIsBriefOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isBasketOpen, setIsBasketOpen] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isProcessOpen, setIsProcessOpen] = useState(false);
    const [isDocsOpen, setIsDocsOpen] = useState(false);
    const [isMessagesOpen, setIsMessagesOpen] = useState(false);
    const [label, setLabel ] = useState('...');
    const [city, setCity] = useState('...');

    // Cutoff pour activer le nouveau questionnaire (v2) uniquement sur les besoins futurs.
    // A remplacer par MAX(need.id) des besoins existants au moment du deploiement prod.
    // Defaut a 999999 = aucun besoin existant ne voit le nouveau questionnaire (safe defaut).
    const QUESTIONNAIRE_V2_CUTOFF_NEED_ID = 577;
    const isNewQuestionnaire = needId > QUESTIONNAIRE_V2_CUTOFF_NEED_ID;

    useEffect(() => {
        switch(activeTab) {
            case 'brief':
                if (!isBriefOpen) setIsBriefOpen(true);
                break;
            case 'search':
                if (!isSearchOpen) setIsSearchOpen(true);
                break;
            case 'basket':
                if (!isBasketOpen) setIsBasketOpen(true);
                break;
            case 'form':
                if (!isFormOpen) setIsFormOpen(true);
                break;
            case 'process':
                if (!isProcessOpen) setIsProcessOpen(true);
                break;
            case 'docs':
                if (!isDocsOpen) setIsDocsOpen(true);
                break;
            case 'messages':
                if (!isMessagesOpen) setIsMessagesOpen(true);
                break;
        }
    }, [activeTab]); // eslint-disable-line

    useEffect(() => {
        fetch('/api/get_need?id=' + needId + '&what=metadata').then(function(r){ return r.json(); }).then(function(d){ if (d && d.data) { if (d.data.client_mission) setLabel(d.data.client_mission); if (d.data.client_localisation) { fetch('https://geo.api.gouv.fr/communes?codePostal=' + d.data.client_localisation + '&fields=nom,population&format=json').then(function(r){ return r.json(); }).then(function(arr){ if (arr && arr.length > 0) { arr.sort(function(a,b){ return (b.population||0) - (a.population||0); }); setCity(arr[0].nom); } }); } } });
    }, [needId]);

    return (
        <>
        <Header needId={needId} handle={(b: boolean) => setIsOpen(b)}/>
        <Center>
            <Badge color='orange' style={{ textTransform: 'none' }}>{label}</Badge>
        </Center>
        <Tabs radius="xs" value={activeTab} onTabChange={setActiveTab}>
            <Tabs.List>
                <Tabs.Tab value="brief" icon={<IconUser size={14} />}>{t('need.brief')}</Tabs.Tab>
                <Tabs.Tab value="search" icon={<IconSearch size={14} />}>{t('need.recherche')}</Tabs.Tab>
                <Tabs.Tab value="basket" icon={<IconBasket size={14} />}>{t('need.panier')}</Tabs.Tab>
                <Tabs.Tab value="form" icon={<IconQuestionMark size={14} />}>Questionnaire</Tabs.Tab>
                <Tabs.Tab value="process" icon={<IconReport size={14} />}>Analyse</Tabs.Tab>
                <Tabs.Tab value="docs" icon={<IconFiles size={14} />}>{t("common.library")}</Tabs.Tab>
                <Tabs.Tab value="messages" icon={<IconMessages size={14} />}>Journal</Tabs.Tab>
            </Tabs.List>

            <ScrollArea style={{width: '100%', height: 'calc(100vh - 170px)'}}>
        
                <Tabs.Panel value="brief">
                    {isBriefOpen &&
                    <Brief needId={needId} isOpen={isOpen} handleLabel={(s: string) => setLabel(s) }/>
                    }
                </Tabs.Panel>

                <Tabs.Panel value="search">
                    {isSearchOpen &&
                    <Search needId={needId} isOpen={isOpen} />
                    }
                </Tabs.Panel>

                <Tabs.Panel value="basket">
                    {isBasketOpen &&
                    <Basket needId={needId} isOpen={isOpen} />
                    }
                </Tabs.Panel>

                <Tabs.Panel value="form">
                    {isFormOpen &&
                    <FormCreator
                        nature='need'
                        id={needId}
                        isOpen={isOpen}
                        initialQuestions={isNewQuestionnaire ? [
                            { position: 1, nature: 'T', required: true , code: '_title', label: 'Mission de ' + label, param: null },
                            { position: 2, nature: 'D', required: true , code: '_date', label: 'Vous êtes disponible à partir du', param: null },
                            { position: 3, nature: 'R', required: true , code: '_move', label: 'La mission se déroule à ' + city + ', Est ce OK pour vous ?', param: null },
                            { position: 4, nature: 'N', required: true , code: '_rate', label: 'Votre TJM hors frais, ou salaire annuel brut, est de', param: null },
                            { position: 5, nature: 'N', required: true , code: '_transitions', label: 'Nombre de missions de transition réalisées ?', param: null },
                            { position: 6, nature: 'N', required: true , code: '_staff', label: 'Effectif moyen managé lors de vos dernières expériences ?', param: null },
                            { position: 7, nature: 'N', required: true , code: '_experience', label: "Nombre d'années d'expérience dans le secteur ...?", param: null },
                            { position: 8, nature: 'N', required: true , code: '_roleexp', label: "Nombre d'années d'expérience comme " + label, param: null },
                            { position: 9, nature: 'Z', required: true , code: '_town', label: 'Votre code postal (mettre 99999 si hors de France)', param: null },
                            { position: 10, nature: 'P', required: true , code: '_phone', label: 'A quel numéro de tél peut-on vous joindre ?', param: null },
                        ] : [
                            { position: 1, nature: 'T', required: true , code: '_title', label: 'Mission de ...', param: null },
                            { position: 2, nature: 'D', required: true , code: '_date', label: 'Vous êtes disponible à partir du', param: null },
                            { position: 3, nature: 'R', required: true , code: '_move', label: 'Vous êtes mobile sur ...', param: null },
                            { position: 4, nature: 'N', required: true , code: '_rate', label: 'Votre TJM hors frais, ou salaire annuel brut, est de', param: null },
                            { position: 5, nature: 'Z', required: false, code: '_town', label: 'Votre code postal (mettre 99999 si hors de France)', param: null },
                        ]}
                    />
                    }
                </Tabs.Panel>

                <Tabs.Panel value="process">
                    {isProcessOpen &&
                    <Process needId={needId} isOpen={isOpen} />
                    }
                </Tabs.Panel>

                <Tabs.Panel value="docs">
                    {isDocsOpen &&
                    <Docs needId={needId} isOpen={isOpen} />
                    }
                </Tabs.Panel>

                <Tabs.Panel value="messages">
                    {isMessagesOpen &&
                    <Messages objectCode='need' objectId={needId} isOpen={isOpen} 
                        getUrl={`get_process_need?need_id=${needId}&what=messages`}
                        updateUrl={`update_process_need?need_id=${needId}&what=message`}
                        height='calc(100vh - 250px)'
                    />
                    }
                </Tabs.Panel>

            </ScrollArea>
        </Tabs>
        </>
    )
}

export { ProcessNeed }